import React from "react";

const ALLOWED_TAGS = [
  "br",
  "b",
  "strong",
  "i",
  "em",
  "a",
  "ul",
  "ol",
  "li",
  "div",
  "p",
  "span",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
];
const VOID_TAGS = ["br"];
const SAFE_STYLE_PROPERTIES = [
  "color",
  "fontWeight",
  "fontStyle",
  "fontSize",
  "fontFamily",
  "opacity",
  "textDecoration",
];
const STYLE_PROPERTY_MAP = {
  color: "color",
  "font-weight": "fontWeight",
  "font-style": "fontStyle",
  "font-size": "fontSize",
  "font-family": "fontFamily",
  opacity: "opacity",
  "text-decoration": "textDecoration",
};

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function serializeUnsupportedNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const tagName = node.tagName.toLowerCase();
  const attrs = Array.from(node.attributes)
    .map((attr) => ` ${attr.name}="${escapeHtml(attr.value)}"`)
    .join("");
  const children = Array.from(node.childNodes)
    .map((child) => serializeUnsupportedNode(child))
    .join("");

  if (VOID_TAGS.includes(tagName)) {
    return `<${tagName}${attrs}>`;
  }

  return `<${tagName}${attrs}>${children}</${tagName}>`;
}

function getSafeHref(value) {
  const href = String(value || "").trim();

  if (!href) {
    return undefined;
  }

  if (/^www\./i.test(href)) {
    return `https://${href}`;
  }

  return href;
}

function getSafeClassName(value) {
  const className = String(value || "")
    .split(/\s+/)
    .filter((item) => /^[a-zA-Z0-9_-]+$/.test(item))
    .join(" ");

  return className || undefined;
}

function getSafeStyle(value) {
  const style = {};

  String(value || "")
    .split(";")
    .forEach((declaration) => {
      const [rawProperty, ...rawValueParts] = declaration.split(":");
      const property = STYLE_PROPERTY_MAP[String(rawProperty || "").trim()];
      const cssValue = rawValueParts.join(":").trim();

      if (!property || !SAFE_STYLE_PROPERTIES.includes(property) || !cssValue) {
        return;
      }

      if (/url\s*\(|expression\s*\(|javascript:/i.test(cssValue)) {
        return;
      }

      style[property] = cssValue;
    });

  return Object.keys(style).length ? style : undefined;
}

function getSafeProps(node) {
  const props = {};
  const tagName = node.tagName.toLowerCase();
  const className = getSafeClassName(node.getAttribute("class"));
  const style = getSafeStyle(node.getAttribute("style"));

  if (className) {
    props.className = className;
  }

  if (style) {
    props.style = style;
  }

  if (tagName === "a") {
    const href = getSafeHref(node.getAttribute("href"));
    if (href) {
      props.href = href;
      props.rel = "noopener noreferrer";
      props.target = "_blank";
    }
  }

  return props;
}

function renderText(value, keyPrefix) {
  return String(value)
    .replace(/\\n/g, "\n")
    .split("\n")
    .flatMap((part, index, parts) => {
      const nodes = [part];

      if (index < parts.length - 1) {
        nodes.push(<br key={`${keyPrefix}-br-${index}`} />);
      }

      return nodes;
    });
}

function renderNode(node, key) {
  if (node.nodeType === Node.TEXT_NODE) {
    return renderText(node.textContent, key);
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const tagName = node.tagName.toLowerCase();

  if (!ALLOWED_TAGS.includes(tagName)) {
    return renderText(serializeUnsupportedNode(node), key);
  }

  if (tagName === "br") {
    return <br key={key} />;
  }

  const children = Array.from(node.childNodes).map((child, index) =>
    renderNode(child, `${key}-${index}`),
  );

  return React.createElement(tagName, { key, ...getSafeProps(node) }, children);
}

export default function renderSafeTypographyContent(content) {
  if (typeof window === "undefined" || !window.DOMParser) {
    return renderText(content || "", "text");
  }

  const normalizedContent = String(content || "").replace(/\\n/g, "\n");
  const doc = new DOMParser().parseFromString(
    `<body>${normalizedContent}</body>`,
    "text/html",
  );

  return Array.from(doc.body.childNodes).map((node, index) =>
    renderNode(node, `typography-${index}`),
  );
}
